import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Paper } from 'shared/ui/Paper';
import { Typography, Divider, Grid, FormControlLabel } from '@mui/material';
import { ColorPalette } from 'shared/consts/colorPalette';
import { SettingsSchemaType } from '../../validationSchema';
import { InputField } from 'shared/ui/InputField';
import { FormSelect } from 'shared/ui/Select/FormSelect';
import { SelectOption } from 'shared/ui/Select/types';
import { FormSwitch } from 'shared/ui/Switch/FormSwitch';
import { useAuthStore } from 'processes/auth/model/state';
import { DefaultExportType } from 'entities/Company/types';

const FtpSettings = () => {
	const { t } = useTranslation();
	const { company } = useAuthStore();
	const { values, setFieldValue } = useFormikContext<SettingsSchemaType>();

	const exportOptions = company?.metadata?.ediFormatCustomModelId
		? [...defaultTypeOptions, EDIOption]
		: [...defaultTypeOptions, XMLOption];

	return (
		<Paper sx={{ width: '100%', padding: 4 }}>
			<Grid container direction="column" gap={2.5}>
				<Typography variant="h5" color={ColorPalette.astronaut}>
					{t('integrations.title')}
				</Typography>

				<Divider color={ColorPalette.periwinkle} />

				<Grid container direction="row" wrap="nowrap" gap={2}>
					<Grid container direction="column" gap={2.5} size={6}>
						<Typography variant="h6" color={ColorPalette.astronaut}>
							{t('integrations.import_folder')}
						</Typography>

						<InputField
							name={'settings.ftpImport.path'}
							label={t('integrations.labels.path')}
							placeholder={t('integrations.placeholder.path')}
							required
						/>

						<FormSelect
							name={'settings.ftpImport.autoImportInterval'}
							label={t('integrations.labels.import_interval') as string}
							placeholder={t('integrations.labels.never')}
							options={optionsForRangeSelect}
							value={values.settings.ftpImport.autoImportInterval}
							onSelect={(value) =>
								setFieldValue('settings.ftpImport.autoImportInterval', value)
							}
						/>

						<FormControlLabel
							sx={{ margin: 0 }}
							control={<FormSwitch name="settings.ftpImport.autoImportDeleteAfter" />}
							label={
								<Typography
									variant={'body2'}
									color={ColorPalette.baliHai}
									sx={{ marginLeft: '10px' }}
								>
									{t('sync_settings.ftp_form.delete_after_import')}
								</Typography>
							}
						/>
					</Grid>

					<Grid container direction="column" gap={2.5} size={6}>
						<Typography variant="h6" color={ColorPalette.astronaut}>
							{t('integrations.export_folder')}
						</Typography>
						<InputField
							name={'settings.ftpExport.path'}
							label={t('integrations.labels.path')}
							placeholder={t('integrations.placeholder.path')}
							required
						/>

						<FormSelect
							name={'settings.ftpExport.autoExportFormat'}
							label={t('integrations.labels.export_type') as string}
							placeholder={`XLSX, XLS, JSON, CSV, ${
								company?.metadata?.ediFormatCustomModelId ? 'EDI' : 'XML'
							}`}
							options={exportOptions}
							value={values.settings.ftpExport.autoExportFormat}
							onSelect={(value) => setFieldValue('settings.ftpExport.autoExportFormat', value)}
						/>

						<InputField
							name={'settings.ftpExport.originalFilePath'}
							label={t('integrations.labels.original_file_path')}
							placeholder={t('integrations.placeholder.original_file_path')}
						/>

						<FormControlLabel
							sx={{ margin: 0 }}
							control={<FormSwitch name="settings.ftpExport.compressOriginalFile" />}
							label={
								<Typography
									variant={'body2'}
									color={ColorPalette.baliHai}
									sx={{ marginLeft: '10px' }}
								>
									{t('integrations.labels.compress_original_file')}
								</Typography>
							}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Paper>
	);
};

export default FtpSettings;

const optionsForRangeSelect: SelectOption[] = [
	{ value: '', label: 'integrations.labels.never' },
	{ value: '3 min', label: '3 min' },
	{ value: '5 min', label: '5 min' },
	{ value: '10 min', label: '10 min' },
	{ value: '15 min', label: '15 min' },
	{ value: '30 min', label: '30 min' },
	{ value: 'hour', label: 'integrations.labels.hour' },
	{ value: 'day', label: 'integrations.labels.day' },
];

const defaultTypeOptions = [
	{ value: '', label: '-' },
	{ value: DefaultExportType.XLSX, label: DefaultExportType.XLSX },
	{ value: DefaultExportType.XLS, label: DefaultExportType.XLS },
	{ value: DefaultExportType.JSON, label: DefaultExportType.JSON },
	{ value: DefaultExportType.CSV, label: DefaultExportType.CSV },
];
const XMLOption = { value: DefaultExportType.XML, label: DefaultExportType.XML };
const EDIOption = { value: DefaultExportType.XML, label: 'EDI' };
